/**
 * SQL token stream → a light tree the layout code can walk.
 *
 * Brackets and CASE … END become groups; compound words (GROUP BY, LEFT JOIN, UNION ALL …)
 * become single tokens; and every word is given the role that decides how it is written
 * (clause, keyword, function, type, or plain identifier). Nothing is parsed beyond that:
 * the tree keeps tokens in their original order, so the formatter can only change
 * whitespace and the case of recognised keywords.
 */

import {
  CLAUSE_WORDS,
  CONSTRAINT_WORDS,
  DDL_WORDS,
  DIALECT_KEYWORDS,
  FUNCTIONS,
  JOIN_MODIFIERS,
  KEYWORDS,
  OPERAND_EXPECTING,
  STATEMENT_WORDS,
  TYPE_NAMES,
  WINDOW_WORDS,
} from "@/lib/tool-kit/latex-dev/sql-keywords";
import type {
  SqlDialect,
  SqlToken,
} from "@/lib/tool-kit/latex-dev/sql-tokenizer";

export type KeywordCase = "upper" | "lower" | "preserve";

export type Role = "clause" | "keyword" | "function" | "type" | "ident";

export interface Leaf {
  kind: "tok";
  tok: SqlToken;
  /** The uppercase form, for words. */
  upper: string;
  /** For a clause word: the canonical clause ("LEFT OUTER JOIN" → "JOIN"); otherwise "". */
  key: string;
  role: Role;
  /** The text to print: keywords carry the chosen case. */
  out: string;
  /** A sign or prefix operator: nothing follows it but the operand. */
  unary: boolean;
}

export type GroupShape = "paren" | "bracket" | "case";
export type GroupRole = "block" | "inline" | "ddl" | "case";

export interface Group {
  kind: "group";
  shape: GroupShape;
  role: GroupRole;
  open: Leaf;
  close: Leaf | null;
  items: Node[];
  /** The bracket sits straight after a name: a call, a type with arguments, an index. */
  attach: boolean;
}

export type Node = Leaf | Group;

const BLOCK_STARTERS: ReadonlySet<string> = new Set([
  "SELECT",
  "WITH",
  "VALUES",
  "UPDATE",
  "INSERT INTO",
  "DELETE FROM",
]);

function leaf(tok: SqlToken): Leaf {
  return {
    kind: "tok",
    tok,
    upper: tok.type === "word" ? tok.text.toUpperCase() : tok.text,
    key: "",
    role: "ident",
    out: tok.text,
    unary: false,
  };
}

// ---------------------------------------------------------------------------
// Compound words
// ---------------------------------------------------------------------------

function wordAt(tokens: SqlToken[], i: number): string | null {
  const tok = tokens[i];
  return tok && tok.type === "word" ? tok.text.toUpperCase() : null;
}

/** How many words after tokens[i] belong with it as one compound word. */
function compoundLength(tokens: SqlToken[], i: number): number {
  const word = wordAt(tokens, i) ?? "";
  const next = wordAt(tokens, i + 1);
  switch (word) {
    case "GROUP":
    case "ORDER":
    case "PARTITION":
      return next === "BY" ? 1 : 0;
    case "UNION":
      return next === "ALL" || next === "DISTINCT" ? 1 : 0;
    case "INTERSECT":
    case "EXCEPT":
      return next === "ALL" ? 1 : 0;
    case "REPLACE":
    case "DELETE":
      return next === (word === "REPLACE" ? "INTO" : "FROM") ? 1 : 0;
    case "INSERT":
      return insertLength(tokens, i);
    case "WITH":
      return next === "RECURSIVE" ? 1 : 0;
    case "ON":
      return onLength(tokens, i);
    case "FOR":
      return next === "UPDATE" || next === "SHARE" ? 1 : 0;
    default:
      return joinLength(tokens, i, word);
  }
}

function insertLength(tokens: SqlToken[], i: number): number {
  if (wordAt(tokens, i + 1) === "INTO") {
    return 1;
  }
  const isOr =
    wordAt(tokens, i + 1) === "OR" && wordAt(tokens, i + 3) === "INTO";
  return isOr ? 3 : 0;
}

function onLength(tokens: SqlToken[], i: number): number {
  const next = wordAt(tokens, i + 1);
  if (next === "CONFLICT") {
    return 1;
  }
  const duplicate =
    next === "DUPLICATE" &&
    wordAt(tokens, i + 2) === "KEY" &&
    wordAt(tokens, i + 3) === "UPDATE";
  return duplicate ? 3 : 0;
}

function joinLength(tokens: SqlToken[], i: number, word: string): number {
  if (word === "STRAIGHT_JOIN") {
    return 0;
  }
  if (!JOIN_MODIFIERS.has(word) && word !== "JOIN") {
    return 0;
  }
  let n = 0;
  let w: string | null = word;
  while (w !== null && JOIN_MODIFIERS.has(w)) {
    n++;
    w = wordAt(tokens, i + n);
  }
  const apply = w === "APPLY";
  if (w === "JOIN" || apply) {
    return n;
  }
  return word === "JOIN" ? 0 : -1;
}

/** Join compound words into single tokens ("GROUP", "BY" → "GROUP BY"). */
export function mergeCompounds(tokens: SqlToken[]): SqlToken[] {
  const out: SqlToken[] = [];
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    const extra = tok.type === "word" ? compoundLength(tokens, i) : 0;
    const take = extra > 0 ? extra : 0;
    const parts = tokens.slice(i, i + take + 1);
    const adjacent = parts.every((p, k) => k === 0 || p.type === "word");
    if (take > 0 && adjacent) {
      out.push({
        type: "word",
        text: parts.map((p) => p.text).join(" "),
        nl: tok.nl,
      });
      i += take + 1;
    } else {
      out.push(tok);
      i++;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

function newGroup(shape: GroupShape, open: Leaf): Group {
  return {
    kind: "group",
    shape,
    role: shape === "case" ? "case" : "inline",
    open,
    close: null,
    items: [],
    attach: false,
  };
}

/** Build the group tree. A stray closing bracket is kept as an ordinary token. */
export function buildTree(tokens: SqlToken[]): Node[] {
  const root: Node[] = [];
  const stack: Group[] = [];
  const current = (): Node[] => stack.at(-1)?.items ?? root;
  for (const tok of tokens) {
    const item = leaf(tok);
    if (tok.type === "open" || tok.type === "bracketOpen") {
      const group = newGroup(tok.type === "open" ? "paren" : "bracket", item);
      current().push(group);
      stack.push(group);
    } else if (tok.type === "word" && item.upper === "CASE") {
      const group = newGroup("case", item);
      current().push(group);
      stack.push(group);
    } else if (closes(stack.at(-1), tok, item)) {
      const group = stack.pop();
      if (group) {
        group.close = item;
      }
    } else {
      current().push(item);
    }
  }
  return root;
}

function closes(top: Group | undefined, tok: SqlToken, item: Leaf): boolean {
  if (!top) {
    return false;
  }
  if (top.shape === "case") {
    return tok.type === "word" && item.upper === "END";
  }
  return (
    (top.shape === "paren" && tok.type === "close") ||
    (top.shape === "bracket" && tok.type === "bracketClose")
  );
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export interface ClassifyOptions {
  dialect: SqlDialect;
  keywordCase: KeywordCase;
}

interface Scope {
  /** The word that opened the statement. */
  stmt: string;
  /** The nodes are the statement itself, not the inside of a bracket. */
  top: boolean;
  /** Inside the column list of a CREATE TABLE. */
  ddlList: boolean;
  /** Inside OVER ( … ). */
  window: boolean;
  /** Inside CAST ( … ). */
  cast: boolean;
}

/** Words capitalised after FETCH (FETCH FIRST 10 ROWS ONLY). */
const FETCH_WORDS: ReadonlySet<string> = new Set([
  "FIRST",
  "NEXT",
  "ROW",
  "ROWS",
  "ONLY",
  "PERCENT",
  "WITH",
  "TIES",
]);

const TYPE_LEADERS: ReadonlySet<string> = new Set(["COLUMN", "ADD", "MODIFY"]);

const CONFLICT_WORDS: ReadonlySet<string> = new Set([
  "DO",
  "NOTHING",
  "UPDATE",
]);

const NULLS_WORDS: ReadonlySet<string> = new Set(["NULLS", "FIRST", "LAST"]);

const DDL_VERBS: ReadonlySet<string> = new Set(["CREATE", "ALTER", "DROP"]);

const NAMED_TABLE_CLAUSES: ReadonlySet<string> = new Set([
  "INSERT INTO",
  "REPLACE INTO",
  "CREATE",
  "ALTER",
  "DROP",
]);

function applyCase(text: string, mode: KeywordCase): string {
  if (mode === "upper") {
    return text.toUpperCase();
  }
  return mode === "lower" ? text.toLowerCase() : text;
}

function isComment(node: Node | undefined): boolean {
  return (
    node?.kind === "tok" &&
    (node.tok.type === "lineComment" || node.tok.type === "blockComment")
  );
}

export function firstSignificant(nodes: Node[]): Node | undefined {
  return nodes.find((n) => !isComment(n));
}

const RE_INSERT_OR = /^INSERT OR \w+ INTO$/;

/** The clause a word starts ("LEFT JOIN" starts a JOIN), or "" when it starts none. */
function clauseKey(
  node: Leaf,
  nodes: Node[],
  scope: Scope,
  clause: string
): string {
  const upper = node.upper;
  const isJoin =
    upper === "JOIN" ||
    upper === "STRAIGHT_JOIN" ||
    upper.endsWith(" JOIN") ||
    upper.endsWith(" APPLY");
  if (isJoin) {
    return "JOIN";
  }
  if (RE_INSERT_OR.test(upper)) {
    return "INSERT INTO";
  }
  return isClauseWord(node, nodes, scope, clause) ? upper : "";
}

function isClauseWord(
  node: Leaf,
  nodes: Node[],
  scope: Scope,
  clause: string
): boolean {
  if (CLAUSE_WORDS.has(node.upper)) {
    // UPDATE starts a statement (or follows a WITH list); in DO UPDATE SET it does not.
    if (node.upper === "UPDATE") {
      const leading = firstSignificant(nodes) === node;
      return scope.top ? clause === "" || clause === "WITH" : leading;
    }
    // WITH TIES, WITH (NOLOCK), WITH CHECK OPTION: only a leading WITH starts a clause.
    if (node.upper.startsWith("WITH")) {
      return firstSignificant(nodes) === node;
    }
    // ALTER TABLE t ALTER COLUMN c SET DEFAULT 1: that SET belongs to the ALTER.
    return !(node.upper === "SET" && DDL_VERBS.has(scope.stmt));
  }
  return (
    scope.top &&
    STATEMENT_WORDS.has(node.upper) &&
    firstSignificant(nodes) === node
  );
}

function isTypePosition(
  index: number,
  nodes: Node[],
  scope: Scope,
  clause: string
): boolean {
  const prev = nodes[index - 1];
  const next = nodes[index + 1];
  if (prev?.kind === "tok" && prev.tok.text === "::") {
    return true;
  }
  if (next?.kind === "tok" && next.tok.type === "string") {
    return true;
  }
  if (scope.cast && prev?.kind === "tok" && prev.upper === "AS") {
    return true;
  }
  const before = nodes[index - 2];
  const prevIsColumn =
    prev?.kind === "tok" &&
    (prev.tok.type === "word" || prev.tok.type === "quoted");
  if (!scope.ddlList) {
    // ALTER TABLE t ADD COLUMN x int: the word after a new column's name.
    return (
      DDL_VERBS.has(clause) &&
      prevIsColumn &&
      before?.kind === "tok" &&
      TYPE_LEADERS.has(before.upper)
    );
  }
  const atItemStart =
    before === undefined ||
    (before.kind === "tok" && before.tok.type === "comma");
  return prevIsColumn && atItemStart;
}

/** Soft keywords that the clause around them makes certain. */
function contextualKeyword(
  node: Leaf,
  prev: Node | undefined,
  clause: string,
  scope: Scope
): boolean {
  const upper = node.upper;
  if (clause === "FETCH" && FETCH_WORDS.has(upper)) {
    return true;
  }
  if (clause === "ON CONFLICT" && CONFLICT_WORDS.has(upper)) {
    return true;
  }
  if (clause === "ORDER BY" || scope.window) {
    const afterNulls = prev?.kind === "tok" && prev.upper === "NULLS";
    if (upper === "NULLS" || (afterNulls && NULLS_WORDS.has(upper))) {
      return true;
    }
  }
  if (scope.window && WINDOW_WORDS.has(upper)) {
    return true;
  }
  if (DDL_VERBS.has(clause) && DDL_WORDS.has(upper)) {
    return true;
  }
  return scope.ddlList && CONSTRAINT_WORDS.has(upper) && prev !== undefined;
}

function wordRole(
  node: Leaf,
  index: number,
  nodes: Node[],
  scope: Scope,
  clause: string,
  opts: ClassifyOptions
): Role {
  if (node.key !== "") {
    return "clause";
  }
  const upper = node.upper;
  const prev = nodes[index - 1];
  const next = nodes[index + 1];
  const callNext = next?.kind === "group" && next.shape === "paren";
  if (TYPE_NAMES.has(upper) && isTypePosition(index, nodes, scope, clause)) {
    return "type";
  }
  if (FUNCTIONS.has(upper) && callNext) {
    return "function";
  }
  const reserved =
    KEYWORDS.has(upper) || DIALECT_KEYWORDS[opts.dialect].has(upper);
  if (reserved || contextualKeyword(node, prev, clause, scope)) {
    return "keyword";
  }
  return upper === "FILTER" && prev?.kind === "group" && callNext
    ? "keyword"
    : "ident";
}

function signOrPrefix(node: Leaf, prev: Node | undefined): boolean {
  const text = node.tok.text;
  if (node.tok.type !== "operator") {
    return false;
  }
  if (text === "~" || text === "!") {
    return true;
  }
  if (text !== "-" && text !== "+") {
    return false;
  }
  if (!prev) {
    return true;
  }
  if (prev.kind === "group") {
    return false;
  }
  const t = prev.tok;
  if (t.type === "operator" || t.type === "comma" || t.type === "open") {
    return t.text !== "::";
  }
  return t.type === "word" && OPERAND_EXPECTING.has(prev.upper);
}

function classifyLeaf(
  node: Leaf,
  index: number,
  nodes: Node[],
  scope: Scope,
  clause: string,
  opts: ClassifyOptions
): void {
  if (node.tok.type === "word") {
    node.key = clauseKey(node, nodes, scope, clause);
    node.role = wordRole(node, index, nodes, scope, clause, opts);
    node.out =
      node.role === "ident"
        ? node.tok.text
        : applyCase(node.tok.text, opts.keywordCase);
  }
  node.unary = signOrPrefix(node, nodes[index - 1]);
}

function attaches(
  prev: Node | undefined,
  scope: Scope,
  clause: string
): boolean {
  if (!prev) {
    return false;
  }
  if (prev.kind === "group") {
    return prev.shape === "bracket";
  }
  const type = prev.tok.type;
  if (type === "quoted") {
    return true;
  }
  if (type !== "word") {
    return false;
  }
  if (prev.role === "function" || prev.role === "type") {
    return true;
  }
  if (prev.role !== "ident") {
    return false;
  }
  // A table name, then its column list: INSERT INTO t (a, b), CREATE TABLE t (…).
  return !(scope.top && NAMED_TABLE_CLAUSES.has(clause));
}

function groupRole(
  group: Group,
  index: number,
  nodes: Node[],
  scope: Scope,
  clause: string
): GroupRole {
  if (group.shape === "case") {
    return "case";
  }
  const first = firstSignificant(group.items);
  const startsBlock =
    first?.kind === "tok" &&
    BLOCK_STARTERS.has(first.upper) &&
    first.role === "clause";
  if (group.shape === "paren" && startsBlock) {
    return "block";
  }
  const earlier = nodes.slice(0, index);
  const isFirstList =
    scope.top &&
    clause === "CREATE" &&
    group.shape === "paren" &&
    earlier.some((n) => n.kind === "tok" && n.upper === "TABLE") &&
    !earlier.some((n) => n.kind === "group" && n.role === "ddl");
  return isFirstList ? "ddl" : "inline";
}

function classifyGroup(
  group: Group,
  index: number,
  nodes: Node[],
  scope: Scope,
  clause: string,
  opts: ClassifyOptions
): void {
  const prev = nodes[index - 1];
  const isBracket = group.shape !== "case";
  group.attach = isBracket && attaches(prev, scope, clause);
  const prevUpper = prev?.kind === "tok" ? prev.upper : "";
  const inner: Scope = {
    stmt: scope.stmt,
    top: false,
    ddlList: false,
    window: prevUpper === "OVER" || (scope.window && group.shape !== "case"),
    cast: prevUpper === "CAST" || prevUpper === "TRY_CAST",
  };
  // Roles of the nested words must be known before this group's own role is.
  classifyList(group.items, inner, opts);
  group.role = groupRole(group, index, nodes, scope, clause);
  if (group.role === "ddl") {
    classifyList(group.items, { ...inner, ddlList: true }, opts);
  }
  if (group.shape === "case") {
    group.open.role = "keyword";
    group.open.out = applyCase(group.open.tok.text, opts.keywordCase);
    if (group.close) {
      group.close.role = "keyword";
      group.close.out = applyCase(group.close.tok.text, opts.keywordCase);
    }
  }
}

function classifyList(
  nodes: Node[],
  scope: Scope,
  opts: ClassifyOptions
): void {
  let clause = "";
  nodes.forEach((node, index) => {
    if (node.kind === "group") {
      classifyGroup(node, index, nodes, scope, clause, opts);
      return;
    }
    classifyLeaf(node, index, nodes, scope, clause, opts);
    if (node.role === "clause") {
      clause = node.key;
    }
  });
}

/** Classify the tokens of one statement. */
export function classifyStatement(nodes: Node[], opts: ClassifyOptions): void {
  const first = firstSignificant(nodes);
  const stmt = first?.kind === "tok" ? first.upper : "";
  classifyList(
    nodes,
    { stmt, top: true, ddlList: false, window: false, cast: false },
    opts
  );
}
